/**
 * fresh-auth（直近 5 分の 2 要素目）の再認証ダイアログ。
 *
 * **無いと詰む。** 設定変更・スタッフ管理・ノード削除・メールボックスの
 * パスワード再発行・秘密の取り出しは `requireFreshAuth()` が付いており、
 * ログインから 5 分過ぎると 401 `REAUTH_REQUIRED` を返す。以前の admin には
 * この出口が無く、しかも API クライアントが 401 を一律「セッション切れ」と
 * 扱っていたため、**押した瞬間にログアウトさせられる**という挙動になっていた。
 *
 * 仕組み:
 *   1. `lib/api.ts` の `setReauthHandler()` にこのコンポーネントを登録する。
 *   2. `REAUTH_REQUIRED` を受けた `request()` がハンドラを await する。
 *   3. 認証コードが通ったら true を返し、`request()` が元の操作を 1 回だけ再送する。
 *
 * 同時に複数のリクエストが 401 を受けることがあるので、待っている解決子は
 * 配列で持ち、1 回の再認証で全部まとめて解決する（ダイアログを何枚も重ねない）。
 */
import * as React from 'react';
import { KeyRound } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mail-sv/ui';
import { setReauthHandler } from '../lib/api.js';
import { reauth } from '../lib/auth.js';
import { Field, NativeSelect } from './form-fields.js';
import { errorMessageOf } from './states.js';

type Resolver = (ok: boolean) => void;

export function ReauthGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [method, setMethod] = React.useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);
  const waiters = React.useRef<Resolver[]>([]);

  React.useEffect(() => {
    setReauthHandler(
      () =>
        new Promise<boolean>((resolve) => {
          waiters.current.push(resolve);
          setError(null);
          setCode('');
          setOpen(true);
        }),
    );
    return () => setReauthHandler(null);
  }, []);

  const settle = React.useCallback((ok: boolean) => {
    const pendingWaiters = waiters.current;
    waiters.current = [];
    setOpen(false);
    setPending(false);
    for (const resolve of pendingWaiters) resolve(ok);
  }, []);

  const submit = (): void => {
    if (pending || code.trim().length === 0) return;
    setPending(true);
    setError(null);
    reauth(method, code.trim()).then(
      () => settle(true),
      (err: unknown) => {
        setPending(false);
        setError(err);
      },
    );
  };

  return (
    <>
      {children}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (pending) return;
          // 閉じる ＝ 再認証しない。待っているリクエストには失敗を返して、
          // 「押したのに何も起きない」まま宙吊りにしない。
          if (!next) settle(false);
        }}
      >
        <DialogContent className="max-w-md" hideClose={pending}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="size-4" aria-hidden />
              本人確認が必要です
            </DialogTitle>
            <DialogDescription>
              この操作は、直近 5 分以内に 2 要素認証を通していることが条件です。
              認証アプリのコードを入力すると、元の操作をそのまま続行します。
            </DialogDescription>
          </DialogHeader>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{errorMessageOf(error)}</AlertDescription>
              </Alert>
            ) : null}

            <Field label="認証方法">
              <NativeSelect
                value={method}
                onChange={(v) => setMethod(v as 'totp' | 'recovery')}
                options={[
                  { value: 'totp', label: '認証アプリ（TOTP）' },
                  { value: 'recovery', label: '復旧コード' },
                ]}
              />
            </Field>

            <Field
              label={method === 'totp' ? '認証コード' : '復旧コード'}
              required
              hint={
                method === 'totp'
                  ? '認証アプリに表示されている 6 桁の数字。'
                  : '登録時に控えた復旧コード。1 度使うと無効になります。'
              }
            >
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                inputMode={method === 'totp' ? 'numeric' : 'text'}
                spellCheck={false}
                autoFocus
              />
            </Field>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => settle(false)} disabled={pending}>
                キャンセル
              </Button>
              <Button type="submit" loading={pending} disabled={code.trim().length === 0}>
                続行する
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
